import React from 'react'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import { themeConstants } from '@/styles/theme-constants.ts'
import { convertPxToNumber } from '@/utils/helpers.ts'
import {
  PODIUM_BORDER_COLOR,
  RankCardData,
  maskedTextStyle,
  inactiveIdentityStyle,
  pnlColor,
} from './ranking-card.types.ts'
import { RankCardColors } from './ranking-card.tsx'
import { ReturnPill } from '@/common/components/return-pill.tsx'
import MedalGold from '@assets/icons/ranking/medal-gold.svg'
import MedalSilver from '@assets/icons/ranking/medal-silver.svg'
import MedalBronze from '@assets/icons/ranking/medal-bronze.svg'

// A podium entry with its metrics pre-formatted by the container.
export interface PodiumItem {
  data: RankCardData
  returnPctText: string
  cumulativePnlText: string
  dailyPnlText: string
}

interface RankingPodiumProps {
  items: PodiumItem[]
  colors: RankCardColors
  dailyLabel: string
  onItemClick?: (key: string) => void
}

// Ordering rank 1 in the centre, 2 on the left, 3 on the right.
const PODIUM_ORDER = [2, 1, 3]

// Per-rank card height (rank 1 tallest).
const PODIUM_HEIGHT: Record<number, number> = {
  1: 160,
  2: 156,
  3: 152,
}

// Per-rank top padding token (rank 1 largest), matching the Figma podium spec.
const PODIUM_PADDING_TOP: Record<number, 'base' | 'md' | 'lg'> = {
  1: 'lg',
  2: 'md',
  3: 'base',
}

// Per-rank content top padding in px (rank 1 largest), used to sink the medal
// exactly this deep so its bottom edge meets the content top without overlap.
const PODIUM_PADDING_TOP_PX: Record<number, number> = {
  1: 24,
  2: 20,
  3: 16,
}

// Medal outer size; the wrapping element renders it at this square size.
const MEDAL_SIZE = 48

// Safety gap so the medal's bottom clears the name's line-box (line-height is
// taller than the glyph, so a flush medal would still clip the top ~2px).
const MEDAL_CLEARANCE = 4

// Daily amount + "Daily" label stacked as one non-shrinking block, so the fixed
// card height can't squeeze the label out.
const DAILY_BLOCK_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  flexShrink: 0,
}

const DAILY_LINE_STYLE: React.CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  lineHeight: '18px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

// SVGs are imported via svgr as React components; render the medal by rank.
// Sizing is handled by the wrapping element (the SVG uses preserveAspectRatio).
export const RankMedal: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank === 1) return <MedalGold />
  if (rank === 2) return <MedalSilver />
  return <MedalBronze />
}

// Podium for ranks 1-3: SumCards topped by a rank medal, rank 1 raised in the
// centre. Pure presentational; the container passes resolved, formatted items.
export const RankingPodium: React.FC<RankingPodiumProps> = React.memo(
  ({ items, colors, dailyLabel, onItemClick }) => {
    const byRank = new Map(items.map((item) => [item.data.rank, item]))
    const paddingTopToken = {
      base: themeConstants.sizeBase,
      md: themeConstants.sizeMD,
      lg: themeConstants.sizeLG,
    }

    return (
      <Row
        gutter={convertPxToNumber(themeConstants.sizeXS)}
        align={'bottom'}
        justify={'center'}
      >
        {PODIUM_ORDER.map((rank) => {
          const item = byRank.get(rank)
          if (!item) return null
          return (
            <Col
              key={item.data.key}
              flex={'1 1 0'}
              style={{
                maxWidth: 140,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingTop:
                  MEDAL_SIZE - PODIUM_PADDING_TOP_PX[3] + MEDAL_CLEARANCE,
              }}
            >
              {/* Medal is absolutely positioned so it overlaps the card edge.
                It sinks exactly the card's padding-top so its bottom meets the
                content top without overlapping the name. */}
              <div
                onClick={
                  onItemClick ? () => onItemClick(item.data.key) : undefined
                }
                style={{
                  position: 'relative',
                  width: '100%',
                  minHeight: PODIUM_HEIGHT[rank],
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: themeConstants.sizeXS,
                  padding: `${paddingTopToken[PODIUM_PADDING_TOP[rank]]} ${themeConstants.sizeXS} ${themeConstants.sizeXS}`,
                  borderRadius: themeConstants.borderRadiusLG,
                  border: `1px solid ${PODIUM_BORDER_COLOR[rank]}`,
                  backgroundColor: colors.cardBg,
                  cursor: onItemClick ? 'pointer' : 'default',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: -(
                      MEDAL_SIZE -
                      PODIUM_PADDING_TOP_PX[rank] +
                      MEDAL_CLEARANCE
                    ),
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: MEDAL_SIZE,
                    height: MEDAL_SIZE,
                    zIndex: 1,
                  }}
                >
                  <RankMedal rank={rank} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: themeConstants.sizeXXS,
                    width: '100%',
                    minWidth: 0,
                    flexShrink: 0,
                  }}
                >
                  {/* Name + score stack: no gap between them (Figma stacks the
                    two 18px lines directly, so the card content fits its height). */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <PodiumIdentity
                      data={item.data}
                      linkColor={colors.linkColor}
                    />
                    {item.data.score && (
                      <IdeyaTypography.Text
                        size={'sm'}
                        style={{
                          color: colors.tertiaryTextColor,
                          height: '18px',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        (★
                        <span
                          style={
                            item.data.isMasked ? maskedTextStyle : undefined
                          }
                        >
                          {item.data.score}
                        </span>
                        )
                      </IdeyaTypography.Text>
                    )}
                  </div>
                  <IdeyaTypography.Text
                    textStyle={'strong'}
                    style={{
                      color: pnlColor(item.data.cumulativePnl, colors),
                      fontVariantNumeric: 'tabular-nums',
                      display: 'block',
                      maxWidth: '100%',
                      height: '20px',
                      minHeight: '20px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.cumulativePnlText}
                  </IdeyaTypography.Text>
                  <div style={{ height: '20px' }}>
                    <ReturnPill
                      value={item.data.returnPct}
                      text={item.returnPctText}
                      colors={colors}
                    />
                  </div>
                </div>
                <div style={DAILY_BLOCK_STYLE}>
                  <IdeyaTypography.Text
                    size={'sm'}
                    textStyle={'strong'}
                    style={{
                      ...DAILY_LINE_STYLE,
                      color: colors.tertiaryTextColor,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {item.dailyPnlText}
                  </IdeyaTypography.Text>
                  <IdeyaTypography.Text
                    size={'sm'}
                    textStyle={'strong'}
                    style={{
                      ...DAILY_LINE_STYLE,
                      color: colors.tertiaryTextColor,
                    }}
                  >
                    {dailyLabel}
                  </IdeyaTypography.Text>
                </div>
              </div>
            </Col>
          )
        })}
      </Row>
    )
  }
)

interface PodiumIdentityProps {
  data: RankCardData
  linkColor?: string
}

// Truncate a long identity to a single line with an ellipsis.
const podiumEllipsisStyle: React.CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: '18px',
}

const PodiumIdentity: React.FC<PodiumIdentityProps> = ({ data, linkColor }) => {
  if (data.isMasked) {
    return (
      <IdeyaTypography.Text size={'sm'} style={podiumEllipsisStyle}>
        <span style={maskedTextStyle}>*****</span>
      </IdeyaTypography.Text>
    )
  }

  if (data.href) {
    return (
      <a
        href={data.href}
        target={'_blank'}
        rel={'noopener noreferrer'}
        title={data.identity}
        style={{
          color: linkColor,
          textDecoration: 'underline',
          textAlign: 'center',
          ...podiumEllipsisStyle,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {data.identity}
      </a>
    )
  }

  return (
    <IdeyaTypography.Text
      size={'sm'}
      style={{
        textAlign: 'center',
        ...podiumEllipsisStyle,
        ...(data.isInactive ? inactiveIdentityStyle : undefined),
      }}
    >
      {data.identity}
    </IdeyaTypography.Text>
  )
}
